import React, { ReactNode } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { FlexBox, FlexBoxProps } from '@lumapps/lumx/react';
import { USER_CARD_POPOVER_CLASSNAME, USER_CARD_POPOVER_MENU_ELEMENT } from '@lumapps/user/constants';
import { UseUserOptions } from '@lumapps/user/types';

import { UserCardPopover, UserCardPopoverTrigger, UserCardPopoverProps, UserCardPopoverTriggerProps } from '.';
import { UseUserCardPopoverResult } from '../../hooks/useUserCardPopover';

export interface UserCardPopoverWrapperProps
    extends UseUserCardPopoverResult,
        UseUserOptions,
        Omit<UserCardPopoverProps, 'isDetailsOpen' | 'isLoading' | 'user' | 'onMouseEnter' | 'onMouseLeave'> {
    /** whether the user card should be displayed on hover or not */
    showUserCardOnHover?: boolean;
    /** additional wrapper props */
    wrapperProps?: Partial<FlexBoxProps>;
    /** additional trigger props */
    triggerProps?: Partial<UserCardPopoverTriggerProps>;
    /** whether the wrapper should be displayed inline or not */
    isInline?: boolean;
    children?: ReactNode;
}

/**
 * This component provides a wrapper for the UserCardPopover component in order to control
 * the display of the user card popover as well as the trigger for accessibility purposes.
 * @param UserCardPopoverProps
 * @returns UserCardPopoverWrapper
 */
export const UserCardPopoverWrapper: React.FC<UserCardPopoverWrapperProps> = ({
    showUserCardOnHover = false,
    children,
    anchorRef,
    showUserCard,
    onClick,
    onMouseLeave,
    onMouseEnter,
    user,
    isLoading,
    asDialog = false,
    isInline = false,
    wrapperProps,
    triggerProps,
    ...options
}) => {
    const { element } = useClassnames(USER_CARD_POPOVER_CLASSNAME);
    const shouldShowUserCard = showUserCardOnHover && user;

    if (showUserCardOnHover) {
        return (
            <FlexBox
                orientation="horizontal"
                hAlign="center"
                as={isInline ? 'span' : 'div'}
                {...wrapperProps}
                className={element('wrapper', { inline: isInline }, [wrapperProps?.className])}
            >
                {children}

                {shouldShowUserCard && (
                    <UserCardPopoverTrigger
                        className={element(USER_CARD_POPOVER_MENU_ELEMENT, { open: showUserCard, asDialog })}
                        onClick={(event: React.SyntheticEvent) => {
                            event.stopPropagation();

                            onClick();
                        }}
                        ref={asDialog ? (anchorRef as React.RefObject<HTMLButtonElement>) : undefined}
                        user={user}
                        {...triggerProps}
                    />
                )}

                {shouldShowUserCard && (
                    <UserCardPopover
                        user={user}
                        isDetailsOpen={showUserCard}
                        anchorRef={anchorRef}
                        onMouseLeave={onMouseLeave}
                        isLoading={isLoading}
                        onMouseEnter={onMouseEnter}
                        asPopoverDialog={asDialog}
                        {...options}
                    />
                )}
            </FlexBox>
        );
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};
